import {
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import ApexCharts, { ApexOptions } from 'apexcharts';
import {
  DashboardFlightListItem,
  ParameterValue,
  ReportRequest,
  UAV,
} from '../../models/report.model';
import { DashboardDataStore } from '../../services/dashboard-data.store';

type FlightTimeBreakdownPieData = {
  labels: string[];
  series: number[];
};

type FlightCountBreakdownRadialData = {
  labels: string[];
  counts: number[];
  series: number[];
  totalCount: number;
};

type DroneUsageSummary = {
  uavLabel: string;
  flightHours: number;
  flightsCount: number;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  private static readonly CHART_COLORS = [
    '#0f766e',
    '#0d9488',
    '#14b8a6',
    '#22c55e',
    '#84cc16',
    '#0ea5e9',
    '#38bdf8',
    '#f59e0b',
    '#f97316',
    '#ef4444',
    '#94a3b8',
  ];

  private flightTimeBreakdownChart: ApexCharts | null = null;
  private flightTimeBreakdownChartHost: HTMLDivElement | null = null;
  private flightCountBreakdownChart: ApexCharts | null = null;
  private flightCountBreakdownChartHostNode: HTMLDivElement | null = null;
  readonly selectedDashboardDateFrom = signal<string>('');
  readonly selectedDashboardDateTo = signal<string>(
    DashboardComponent.currentDateInputValue(),
  );
  readonly canResetDateFilters = computed(
    () =>
      this.selectedDashboardDateFrom() !== '' ||
      this.selectedDashboardDateTo() !==
        DashboardComponent.currentDateInputValue(),
  );
  readonly selectedDashboardDateFromValue = computed<Date | null>(() =>
    this.parseDateInputValue(this.selectedDashboardDateFrom()),
  );
  readonly selectedDashboardDateToValue = computed<Date | null>(() =>
    this.parseDateInputValue(this.selectedDashboardDateTo()),
  );
  readonly selectedFlightDateRangeSubtitle = computed<string | null>(() => {
    const fromDate = this.selectedDashboardDateFromValue();
    const toDate = this.selectedDashboardDateToValue();

    if (!fromDate || !toDate) {
      return null;
    }

    const [rangeStart, rangeEnd] =
      fromDate.getTime() <= toDate.getTime()
        ? [fromDate, toDate]
        : [toDate, fromDate];

    return `${this.formatDateForSubtitle(rangeStart)} - ${this.formatDateForSubtitle(rangeEnd)}`;
  });
  readonly selectedFlightDateRangeSuffix = computed(() => {
    const rangeSubtitle = this.selectedFlightDateRangeSubtitle();
    return rangeSubtitle ? ` · ${rangeSubtitle}` : '';
  });

  @ViewChild('flightTimeBreakdownChartHost')
  set flightTimeBreakdownChartHostRef(
    hostRef: ElementRef<HTMLDivElement> | undefined,
  ) {
    this.flightTimeBreakdownChartHost = hostRef?.nativeElement ?? null;
    this.syncFlightTimeBreakdownChart(
      this.flightTimeBreakdownChartData(),
      this.dashboardDataStore.loading(),
      this.dashboardDataStore.loadError() !== null,
    );
  }

  @ViewChild('flightCountBreakdownChartHost')
  set flightCountBreakdownChartHost(
    hostRef: ElementRef<HTMLDivElement> | undefined,
  ) {
    this.flightCountBreakdownChartHostNode = hostRef?.nativeElement ?? null;
    this.syncFlightCountBreakdownChart(
      this.flightCountBreakdownChartData(),
      this.dashboardDataStore.loading(),
      this.dashboardDataStore.loadError() !== null,
    );
  }

  readonly flightTimeBreakdownChartData =
    computed<FlightTimeBreakdownPieData | null>(() => {
      if (this.dashboardDataStore.highestFlightsLoadError()) {
        return null;
      }

      const totalFlightSeconds =
        this.dashboardDataStore.brief().totalFlightSeconds;
      if (totalFlightSeconds <= 0) {
        return null;
      }

      const flightSegments = this.dashboardDataStore
        .highestFlightItems()
        .map((item, index) => this.mapFlightTimeSegment(item, index))
        .filter(
          (
            segment,
          ): segment is {
            label: string;
            seconds: number;
          } => segment !== null,
        );

      const labels = flightSegments.map((segment) => segment.label);
      const series = flightSegments.map((segment) =>
        this.secondsToHours(segment.seconds),
      );
      const topDronesFlightSeconds = flightSegments.reduce(
        (sum, segment) => sum + segment.seconds,
        0,
      );
      const otherFlightSeconds = Math.max(
        0,
        totalFlightSeconds - topDronesFlightSeconds,
      );

      if (otherFlightSeconds > 0 || series.length === 0) {
        labels.push(this.translate.instant('dashboard.labels.others'));
        series.push(this.secondsToHours(otherFlightSeconds));
      }

      return { labels, series };
    });

  readonly flightCountBreakdownChartData =
    computed<FlightCountBreakdownRadialData | null>(() => {
      if (this.dashboardDataStore.highestFlightsLoadError()) {
        return null;
      }

      const totalFlightsCount =
        this.dashboardDataStore.brief().totalFlightsCount;
      if (totalFlightsCount <= 0) {
        return null;
      }

      const countSegments = this.dashboardDataStore
        .highestFlightItems()
        .map((item, index) => this.mapCountSegment(item, index))
        .filter(
          (
            segment,
          ): segment is {
            label: string;
            count: number;
          } => segment !== null,
        );

      const labels = countSegments.map((segment) => segment.label);
      const counts = countSegments.map((segment) => segment.count);
      const topDronesFlightsCount = countSegments.reduce(
        (sum, segment) => sum + segment.count,
        0,
      );
      const otherFlightsCount = Math.max(
        0,
        totalFlightsCount - topDronesFlightsCount,
      );

      if (otherFlightsCount > 0 || counts.length === 0) {
        labels.push(this.translate.instant('dashboard.labels.others'));
        counts.push(otherFlightsCount);
      }

      return {
        labels,
        counts,
        series: counts.map((count) => this.toPercent(count, totalFlightsCount)),
        totalCount: totalFlightsCount,
      };
    });

  readonly canShowFlightCountBreakdownChart = computed(
    () => this.flightCountBreakdownChartData() !== null,
  );

  readonly canShowFlightTimeBreakdownChart = computed(
    () => this.flightTimeBreakdownChartData() !== null,
  );

  readonly mostUsedDrone = computed<DroneUsageSummary | null>(() => {
    if (this.dashboardDataStore.highestFlightsLoadError()) {
      return null;
    }

    const item = this.dashboardDataStore.highestFlightItems()[0];
    return item ? this.mapDroneUsageSummary(item) : null;
  });

  readonly leastUsedDrone = computed<DroneUsageSummary | null>(() => {
    if (this.dashboardDataStore.lowestFlightsLoadError()) {
      return null;
    }

    const item = this.dashboardDataStore.lowestFlightItems()[0];
    return item ? this.mapDroneUsageSummary(item) : null;
  });

  readonly flightCountBreakdownUnavailableReason = computed(() => {
    if (this.dashboardDataStore.highestFlightsLoadError()) {
      return 'dashboard.messages.breakdown_unavailable_highest_failed';
    }

    if (this.dashboardDataStore.brief().totalFlightsCount <= 0) {
      return 'dashboard.messages.no_flight_count_data';
    }

    return null;
  });

  readonly flightTimeBreakdownUnavailableReason = computed(() => {
    if (this.dashboardDataStore.highestFlightsLoadError()) {
      return 'dashboard.messages.breakdown_unavailable_highest_failed';
    }

    if (this.dashboardDataStore.brief().totalFlightSeconds <= 0) {
      return 'dashboard.messages.no_flight_time_data';
    }

    return null;
  });

  constructor(
    public readonly dashboardDataStore: DashboardDataStore,
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {
    effect(() => {
      const flightTimeChartData = this.flightTimeBreakdownChartData();
      const flightCountChartData = this.flightCountBreakdownChartData();
      const loading = this.dashboardDataStore.loading();
      const hasFatalError = this.dashboardDataStore.loadError() !== null;
      this.syncFlightTimeBreakdownChart(
        flightTimeChartData,
        loading,
        hasFatalError,
      );
      this.syncFlightCountBreakdownChart(
        flightCountChartData,
        loading,
        hasFatalError,
      );
    });
  }

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroyFlightTimeBreakdownChart();
    this.destroyFlightCountBreakdownChart();
  }

  loadDashboardData(): void {
    this.dashboardDataStore.loadDashboardData(
      this.buildDashboardReportRequest(),
    );
  }

  onDashboardDateFromChange(dateValue: Date | null): void {
    this.selectedDashboardDateFrom.set(this.toDateInputValue(dateValue));
    this.loadFlightReportsForDateFilter();
  }

  onDashboardDateToChange(dateValue: Date | null): void {
    this.selectedDashboardDateTo.set(this.toDateInputValue(dateValue));
    this.loadFlightReportsForDateFilter();
  }

  resetDateFilters(): void {
    this.selectedDashboardDateFrom.set('');
    this.selectedDashboardDateTo.set(
      DashboardComponent.currentDateInputValue(),
    );
    this.loadFlightReportsForDateFilter();
  }

  goToLogin(): void {
    void this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url || '/dashboard' },
    });
  }

  private syncFlightTimeBreakdownChart(
    chartData: FlightTimeBreakdownPieData | null,
    loading: boolean,
    hasFatalError: boolean,
  ): void {
    if (
      !this.flightTimeBreakdownChartHost ||
      loading ||
      hasFatalError ||
      chartData === null
    ) {
      this.destroyFlightTimeBreakdownChart();
      return;
    }

    this.destroyFlightTimeBreakdownChart();
    this.flightTimeBreakdownChart = new ApexCharts(
      this.flightTimeBreakdownChartHost,
      this.buildPieOptions(chartData),
    );
    void this.flightTimeBreakdownChart.render();
  }

  private syncFlightCountBreakdownChart(
    chartData: FlightCountBreakdownRadialData | null,
    loading: boolean,
    hasFatalError: boolean,
  ): void {
    if (
      !this.flightCountBreakdownChartHostNode ||
      loading ||
      hasFatalError ||
      chartData === null
    ) {
      this.destroyFlightCountBreakdownChart();
      return;
    }

    this.destroyFlightCountBreakdownChart();
    this.flightCountBreakdownChart = new ApexCharts(
      this.flightCountBreakdownChartHostNode,
      this.buildRadialBarOptions(chartData),
    );
    void this.flightCountBreakdownChart.render();
  }

  private buildPieOptions(chartData: FlightTimeBreakdownPieData): ApexOptions {
    return {
      chart: {
        type: 'pie',
        height: 300,
      },
      labels: chartData.labels,
      series: chartData.series,
      colors: DashboardComponent.CHART_COLORS.slice(0, chartData.series.length),
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '12px',
      },
      dataLabels: {
        enabled: true,
        formatter: (value: number) => `${value.toFixed(1)}%`,
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        fillSeriesColor: false,
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
        y: {
          formatter: (value: number) =>
            `${value.toFixed(2)} ${this.translate.instant('dashboard.labels.hours')}`,
        },
      },
      stroke: {
        colors: ['#ffffff'],
        width: 1,
      },
    };
  }

  private buildRadialBarOptions(
    chartData: FlightCountBreakdownRadialData,
  ): ApexOptions {
    return {
      chart: {
        type: 'radialBar',
        height: 300,
      },
      labels: chartData.labels,
      series: chartData.series,
      colors: DashboardComponent.CHART_COLORS.slice(0, chartData.series.length),
      plotOptions: {
        radialBar: {
          inverseOrder: true,
          hollow: {
            size: '35%',
          },
          track: {
            background: '#e2e8f0',
            margin: 8,
            strokeWidth: '100%',
          },
          dataLabels: {
            name: {
              show: true,
              fontSize: '14px',
            },
            value: {
              show: true,
              fontSize: '14px',
              formatter: (value: number) => `${value.toFixed(1)}%`,
            },
            total: {
              show: true,
              label: this.translate.instant('dashboard.labels.total'),
              formatter: () => `${chartData.totalCount}`,
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '12px',
        formatter: (seriesName, opts) => {
          const index = opts.seriesIndex;
          const count = chartData.counts[index] ?? 0;
          const percent = chartData.series[index] ?? 0;
          return `${seriesName}: ${count} ${this.translate.instant(
            'dashboard.labels.flights',
          )} (${percent.toFixed(1)}%)`;
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        fillSeriesColor: false,
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
        y: {
          formatter: (value: number, opts?: { seriesIndex: number }) => {
            const count = chartData.counts[opts?.seriesIndex ?? 0] ?? 0;
            return `${count} ${this.translate.instant(
              'dashboard.labels.flights',
            )} (${value.toFixed(1)}%)`;
          },
        },
      },
      stroke: {
        colors: ['#ffffff'],
        width: 2,
        lineCap: 'round',
      },
    };
  }

  private mapCountSegment(
    item: DashboardFlightListItem,
    index: number,
  ): {
    label: string;
    count: number;
  } | null {
    const count = this.toPositiveNumber(item.flightsCount);
    if (count <= 0) {
      return null;
    }

    return {
      label: `#${index + 1} ${this.formatUavLabel(item.uav)}`,
      count,
    };
  }

  private mapFlightTimeSegment(
    item: DashboardFlightListItem,
    index: number,
  ): {
    label: string;
    seconds: number;
  } | null {
    const seconds = this.toPositiveNumber(item.flightTime);
    if (seconds <= 0) {
      return null;
    }

    return {
      label: `#${index + 1} ${this.formatUavLabel(item.uav)}`,
      seconds,
    };
  }

  private mapDroneUsageSummary(
    item: DashboardFlightListItem,
  ): DroneUsageSummary {
    return {
      uavLabel: this.formatUavLabel(item.uav),
      flightHours: this.secondsToHours(this.toPositiveNumber(item.flightTime)),
      flightsCount: this.toPositiveNumber(item.flightsCount),
    };
  }

  private toPercent(value: number, total: number): number {
    if (total <= 0) {
      return 0;
    }

    return Math.max(0, (value / total) * 100);
  }

  private secondsToHours(seconds: number): number {
    return seconds / 3600;
  }

  private toPositiveNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, value);
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }

    return 0;
  }

  private formatUavLabel(uav: UAV): string {
    const hardwareRevision = this.toFiniteNumber(uav.hardwareRevision);
    const unitId = this.toFiniteNumber(uav.unitId);
    // Reports are always queried with resolveDevices=true, so the model name comes from the device
    // the backend resolved. There is deliberately no local hardware-revision lookup table here: it
    // would be a second, drifting copy of the backend model registry.
    const modelName = uav.modelName;

    if (unitId !== null && modelName) {
      return `${unitId} (${modelName})`;
    }

    if (unitId !== null && hardwareRevision !== null) {
      return `${unitId} (HR ${hardwareRevision})`;
    }

    if (unitId !== null) {
      return `${unitId}`;
    }

    if (modelName) {
      return modelName;
    }

    if (hardwareRevision !== null) {
      return `HR ${hardwareRevision}`;
    }

    return this.translate.instant('dashboard.labels.unknown_uav');
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private destroyFlightTimeBreakdownChart(): void {
    if (!this.flightTimeBreakdownChart) {
      return;
    }

    void this.flightTimeBreakdownChart.destroy();
    this.flightTimeBreakdownChart = null;
  }

  private destroyFlightCountBreakdownChart(): void {
    if (!this.flightCountBreakdownChart) {
      return;
    }

    void this.flightCountBreakdownChart.destroy();
    this.flightCountBreakdownChart = null;
  }

  private buildDashboardReportRequest(): ReportRequest {
    const fromStart = this.buildUtcDayBoundary(
      this.selectedDashboardDateFrom(),
      'start',
    );
    const toEnd = this.buildUtcDayBoundary(
      this.selectedDashboardDateTo(),
      'end',
    );

    if (!fromStart && !toEnd) {
      return {};
    }

    let resolvedFrom = fromStart;
    let resolvedTo = toEnd;
    if (
      resolvedFrom &&
      resolvedTo &&
      resolvedFrom.getTime() > resolvedTo.getTime()
    ) {
      const previousFrom = resolvedFrom;
      resolvedFrom = resolvedTo;
      resolvedTo = previousFrom;
    }

    const parameterValues: ParameterValue[] = [];
    if (resolvedFrom) {
      parameterValues.push({
        name: 'dateFrom',
        value: resolvedFrom.toISOString(),
      });
    }
    if (resolvedTo) {
      parameterValues.push({
        name: 'dateTo',
        value: resolvedTo.toISOString(),
      });
    }

    return { parameterValues };
  }

  private loadFlightReportsForDateFilter(): void {
    this.dashboardDataStore.loadDashboardData(
      this.buildDashboardReportRequest(),
      {
        includeServiceReports: false,
      },
    );
  }

  private buildUtcDayBoundary(
    dateValue: string,
    boundary: 'start' | 'end',
  ): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(monthIndex) ||
      !Number.isInteger(day)
    ) {
      return null;
    }

    if (boundary === 'start') {
      return new Date(Date.UTC(year, monthIndex, day, 0, 0, 0, 0));
    }

    return new Date(Date.UTC(year, monthIndex, day, 23, 59, 59, 999));
  }

  private static currentDateInputValue(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDateInputValue(dateValue: Date | null): string {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
      return '';
    }

    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateInputValue(dateValue: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(monthIndex) ||
      !Number.isInteger(day)
    ) {
      return null;
    }

    return new Date(year, monthIndex, day);
  }

  private formatDateForSubtitle(date: Date): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
    }).format(date);
  }
}
